import type { Theme } from "@mui/material";
import { Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import {
	ClientPortfolioDomainEnum,
	type CurrencyEnum,
	type PortfolioListItemResponseDTO,
	type PortfolioListReportingSummaryResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	type useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ListItemMoneyValue } from "web_myplann/Shared/Components/ListItemMoneyValue";
import { PercentageDelta } from "web_myplann/Shared/Components/PercentageDelta";
import { dateFormatter } from "web_myplann/Shared/Utils/formatters";
import { roundNumber } from "web_myplann/Utils/formatters";

const INSURANCE_DOMAINS = new Set([
	ClientPortfolioDomainEnum.LifeInsurance,
	ClientPortfolioDomainEnum.PropertyInsurance,
]);

const INVESTMENT_DOMAINS = new Set([
	ClientPortfolioDomainEnum.PensionProducts,
	ClientPortfolioDomainEnum.BuildingSavings,
	ClientPortfolioDomainEnum.Investments,
]);

const LOAN_DOMAINS = new Set([
	ClientPortfolioDomainEnum.BusinessLoans,
	ClientPortfolioDomainEnum.Loans,
	ClientPortfolioDomainEnum.Leasing,
]);

const ASSET_DOMAINS = new Set([
	ClientPortfolioDomainEnum.Commodities,
	ClientPortfolioDomainEnum.BankingProducts,
	ClientPortfolioDomainEnum.Cryptocurrencies,
]);

const SERVICE_DOMAINS = new Set([
	ClientPortfolioDomainEnum.Services,
	ClientPortfolioDomainEnum.PaidAdvisory,
]);

type TranslateFunction = ReturnType<typeof useResource>["t"];

function normalizeChange(change: number | null | undefined): number | null {
	if (change == null) {
		return null;
	}
	const rounded = roundNumber(change);
	return rounded <= 0.01 && rounded >= -0.01 ? 0 : rounded;
}

function getCurrencySign(t: TranslateFunction, currency: CurrencyEnum): string {
	return (
		t(Resources.PortfolioDetail.SharedLabels.Currency[currency]) ??
		t(Resources.Common.CurrencySign)
	);
}

function renderInsuranceRow(
	data: PortfolioListItemResponseDTO,
	t: TranslateFunction,
	theme: Theme,
): ReactNode {
	let dateRange = "";
	if (data.startAt) {
		const endDate = data.endAt
			? dateFormatter(data.endAt)
			: t(Resources.Common.NotSpecified);
		dateRange = `${dateFormatter(data.startAt)} - ${endDate}`;
	}

	return (
		<Typography
			variant="deprecatedBody1"
			color={theme.palette.deprecated.grey[500]}
		>
			{dateRange}
		</Typography>
	);
}

function renderValueWithDelta(
	currentValue: number | null,
	change: number | null,
	currencySign: string,
): ReactNode {
	return (
		<ListItemMoneyValue
			value={currentValue}
			currencySign={currencySign}
			adornment={
				currentValue != null && currentValue !== 0 ? (
					<PercentageDelta change={change} />
				) : undefined
			}
		/>
	);
}

function renderValueOnly(
	currentValue: number | null,
	currencySign: string,
): ReactNode {
	return (
		<ListItemMoneyValue value={currentValue} currencySign={currencySign} />
	);
}

function renderServiceRow(
	data: PortfolioListItemResponseDTO,
	theme: Theme,
): ReactNode {
	return (
		<Stack direction="row" gap={0.8}>
			<Typography
				variant="deprecatedBody1"
				color={theme.palette.deprecated.grey[500]}
				sx={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					maxWidth: "100%",
				}}
			>
				{data.contractNumber}
			</Typography>
		</Stack>
	);
}

export function renderThirdRow(
	data: PortfolioListItemResponseDTO,
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined,
	t: TranslateFunction,
	theme: Theme,
): ReactNode {
	const domain = data.domain;
	if (domain == null) {
		return null;
	}

	if (INSURANCE_DOMAINS.has(domain)) {
		return renderInsuranceRow(data, t, theme);
	}

	if (SERVICE_DOMAINS.has(domain)) {
		return renderServiceRow(data, theme);
	}

	const summary = summaryData?.find((item) => item.id === data.id)?.summary;

	const rawValue = summary?.totalValue ?? null;
	const currentValue = rawValue == null ? null : roundNumber(rawValue, 0);
	const currencySign = summary
		? getCurrencySign(t, summary.currency)
		: t(Resources.Common.CurrencySign);

	if (INVESTMENT_DOMAINS.has(domain) || ASSET_DOMAINS.has(domain)) {
		const change = normalizeChange(summary?.totalEvaluationPercent);
		return renderValueWithDelta(currentValue, change, currencySign);
	}

	if (LOAN_DOMAINS.has(domain)) {
		return renderValueOnly(currentValue, currencySign);
	}

	return null;
}

export function renderReportingThirdRow(
	data: PortfolioListItemResponseDTO,
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined,
	t: TranslateFunction,
): ReactNode {
	const summary = summaryData?.find((item) => item.id === data.id)?.summary;

	const rawValue = summary?.totalValue ?? null;
	const currentValue = rawValue == null ? null : roundNumber(rawValue, 0);
	const currencySign = summary
		? getCurrencySign(t, summary.currency)
		: t(Resources.Common.CurrencySign);
	const change = normalizeChange(summary?.totalEvaluationPercent);
	return renderValueWithDelta(currentValue, change, currencySign);
}

export function getTitle(
	data: PortfolioListItemResponseDTO,
	t: TranslateFunction,
): string {
	const title = data.name ?? data.product;
	if (title) {
		return title;
	}
	return data.domain != null
		? t(Resources.Portfolio.List.PortfolioDomainType[data.domain])
		: "";
}
